import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Share } from 'react-native';
import { blockUser, buildPublicProfileUrl, getUserPublicProfile } from '../api/client';
import type { UserPublic } from '../api/types';
import { UserAvatarPreviewModal, type UserAvatarPreviewTarget } from '../components/UserAvatarPreviewModal';
import { useLocale } from '../i18n/LocaleContext';
import type { MessageKey } from '../i18n/messages';
import type { RootStackParamList } from '../navigation/types';
import { notifyAction } from '../util/confirmDialog';
import { humanizeError } from '../util/humanizeError';
import { showToast } from '../util/toastBridge';
import type { PersonRelation } from './contactsModel';
import { refreshContacts, removeContactById } from './contactsStore';
import { startCallWithUser } from './directCall';

export type ProfilePerson = {
  userId: string;
  displayName: string;
  hasAvatar?: boolean | null;
  avatarRevision?: number | null;
  avatarShape?: string | null;
  publicHandle?: string | null;
};

type Options = {
  /** Открыть личный чат с человеком — экран сам решает, заменить ли себя чатом. */
  onWrite: (person: ProfilePerson) => void;
  /** Отправить заявку (у незнакомца). */
  onAddContact?: (person: ProfilePerson) => void;
  /** «Поделиться контактом» в чат — только там, где есть выбор чата (список чатов). */
  onShareContact?: (profile: UserPublic, person: ProfilePerson) => void;
};

/**
 * Карточка человека как профиль (вариант A аудита 2026-10-04): «Написать / Позвонить / Видео»,
 * а в «⋯» — поделиться, удалить из контактов, заблокировать.
 *
 * <p>Один хук на два экрана (список чатов и «Новое сообщение»): какие кнопки у карточки, решает
 * то, кем человек приходится смотрящему, а не то, откуда её открыли. Раньше в карточке друга не
 * было ни звонка, ни удаления, ни блокировки, а удалить друга можно было только скрытым свайпом.
 */
export function useProfileCard(
  navigation: Pick<NativeStackNavigationProp<RootStackParamList>, 'navigate'>,
  options: Options,
) {
  const { t } = useLocale();
  const [card, setCard] = useState<UserAvatarPreviewTarget | null>(null);
  /**
   * Обработчики экрана — через ссылку: экран передаёт их новыми замыканиями на каждом рендере,
   * а `open` попадает в зависимости строк списка, и пересоздавать из-за этого все видимые
   * строки незачем. Читаются они в момент нажатия, то есть всегда свежие.
   */
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const fail = useCallback(
    (e: unknown, fallback: MessageKey) => {
      void notifyAction({ title: t('errorTitle'), body: humanizeError(e, t(fallback)), okText: t('dialogOk') });
    },
    [t],
  );

  const open = useCallback(
    (person: ProfilePerson, relation: PersonRelation) => {
      const isContact = relation === 'contact';
      const call = (mode: 'audio' | 'video') => () => {
        startCallWithUser(person, mode).catch((e: unknown) => fail(e, 'openChatFailed'));
      };
      const shareLink = (handle: string | null | undefined) =>
        handle
          ? () => {
              const url = buildPublicProfileUrl(handle);
              void Share.share({ message: url, url }).catch(() => undefined);
            }
          : undefined;
      setCard({
        userId: person.userId,
        displayName: person.displayName,
        hasAvatar: person.hasAvatar ?? undefined,
        revision: person.avatarRevision ?? 0,
        shapeId: person.avatarShape ?? null,
        publicHandle: person.publicHandle ?? null,
        // Крупное фото — друзьям и себе; найденному поиском незнакомцу — до принятой заявки нет.
        canViewPhoto: isContact || relation === 'self',
        onWriteMessage: isContact ? () => optionsRef.current.onWrite(person) : undefined,
        onCall: isContact ? call('audio') : undefined,
        onVideoCall: isContact ? call('video') : undefined,
        onAddContact:
          (relation === 'stranger' || relation === 'outgoing') && optionsRef.current.onAddContact
            ? () => optionsRef.current.onAddContact?.(person)
            : undefined,
        contactPending: relation === 'outgoing',
        onShareLink: shareLink(person.publicHandle),
        onRemoveContact: isContact
          ? async () => {
              try {
                await removeContactById(person.userId);
                showToast({ message: t('profileRemovedToast') });
              } catch (e) {
                fail(e, 'contactRemoveFailed');
                throw e;
              }
            }
          : undefined,
        onBlock:
          relation === 'self'
            ? undefined
            : async () => {
                try {
                  await blockUser(person.userId);
                  showToast({ message: t('blockDone') });
                  void refreshContacts();
                } catch (e) {
                  fail(e, 'blockFailed');
                  throw e;
                }
              },
      });
      // Профиль дотягиваем: из него статус, «о себе», день рождения и право делиться контактом.
      void getUserPublicProfile(person.userId)
        .then((profile) => {
          setCard((prev) =>
            prev && prev.userId === person.userId
              ? {
                  ...prev,
                  displayName: profile.displayName || prev.displayName,
                  hasAvatar: profile.hasAvatar,
                  revision: profile.avatarRevision ?? prev.revision,
                  shapeId: profile.avatarShape ?? prev.shapeId,
                  publicHandle: profile.publicHandle ?? prev.publicHandle,
                  bio: profile.bio ?? null,
                  statusText: profile.statusText ?? null,
                  gender: profile.gender ?? null,
                  age: profile.age ?? null,
                  birthDay: profile.birthDay ?? null,
                  birthMonth: profile.birthMonth ?? null,
                  onShareLink: prev.onShareLink ?? shareLink(profile.publicHandle),
                  // Делиться можно только открытым профилем (стори 5.11) и только другом.
                  ...(isContact && optionsRef.current.onShareContact && profile.profilePublic !== false
                    ? { onShareContact: () => optionsRef.current.onShareContact?.(profile, person) }
                    : {}),
                }
              : prev,
          );
        })
        .catch(() => {
          /* карточка остаётся с тем, что уже известно */
        });
    },
    [fail, t],
  );

  const close = useCallback(() => setCard(null), []);

  const element = useMemo(
    () => <UserAvatarPreviewModal visible={card != null} onClose={close} target={card} />,
    [card, close],
  );

  return useMemo(() => ({ open, close, element }), [close, element, open]);
}
